import { useState } from 'react';
import { ParsedImportCard } from 'utils/importSessionStorage';
import { calculateImportSummary } from 'utils/universalCsvParser';
import { AssetUrls } from 'config/assets';
import MagicButton from 'components/MagicButton';

interface CardPreviewProps {
  cards: ParsedImportCard[];
  totalCards?: number | null; // Total cards in import (may be more than cards array for large imports)
  condition: string;
  onBack: () => void;
  onImport: () => void;
  isAuthenticated: boolean;
  isLoading?: boolean;
}

const CONDITION_LABELS: Record<string, string> = {
  NEAR_MINT: 'Near Mint',
  LIGHTLY_PLAYED: 'Lightly Played',
  MODERATELY_PLAYED: 'Moderately Played',
  HEAVILY_PLAYED: 'Heavily Played',
  DAMAGED: 'Damaged',
};

// Show ~2 rows of cards initially (8 cards fits 2 rows on mobile with 4 columns)
const INITIAL_DISPLAY_COUNT = 8;
const LOAD_MORE_COUNT = 20;

const CardPreview = ({
  cards,
  totalCards,
  condition,
  onBack,
  onImport,
  isAuthenticated,
  isLoading = false,
}: CardPreviewProps) => {
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const displayCards = cards.slice(0, displayCount);
  const hasMore = cards.length > displayCount;
  const summary = calculateImportSummary(cards);

  // For session-based imports, totalCards prop is the unique card count from backend
  // Always use summary.totalCards for the actual quantity sum
  const totalQuantity = summary.totalCards; // Sum of all quantities (e.g., 147)
  const uniqueCardCount = totalCards || summary.uniqueCards; // Unique cards (e.g., 98)
  const isPreviewOnly = totalCards && totalCards > cards.length;

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + LOAD_MORE_COUNT);
  };

  const handleImageError = (scryfallUuid: string) => {
    setImageErrors((prev) => new Set(prev).add(scryfallUuid));
  };

  const getCardImageUrl = (card: ParsedImportCard): string => {
    if (imageErrors.has(card.scryfallUuid)) {
      return AssetUrls.cards.defaultBack;
    }
    return AssetUrls.cards.getCardImage(card.scryfallUuid, 'small');
  };

  return (
    <div className="import-preview">
      <div className="import-preview-header">
        <h2>Preview Your Import</h2>
        <div className="import-preview-summary">
          <div className="import-summary-stat">
            <span className="import-summary-value">{uniqueCardCount.toLocaleString()}</span>
            <span className="import-summary-label">Unique Cards{isPreviewOnly && '*'}</span>
          </div>
          <div className="import-summary-stat">
            <span className="import-summary-value">{totalQuantity.toLocaleString()}</span>
            <span className="import-summary-label">Total Cards{isPreviewOnly && '*'}</span>
          </div>
          {summary.totalFoil > 0 && (
            <div className="import-summary-stat">
              <span className="import-summary-value">{summary.totalFoil.toLocaleString()}</span>
              <span className="import-summary-label">Foil{isPreviewOnly && '*'}</span>
            </div>
          )}
        </div>
        <div className="import-preview-condition">
          Condition: <strong>{CONDITION_LABELS[condition] || condition}</strong>
          <button className="import-change-condition-btn" onClick={onBack} type="button">
            Change
          </button>
        </div>
      </div>

      {/* CTA button above the fold */}
      <div className="import-preview-actions">
        <MagicButton
          label={
            isLoading
              ? 'Importing...'
              : isAuthenticated
                ? `Import ${totalQuantity.toLocaleString()} Cards →`
                : 'Sign in and Import →'
          }
          color="purple"
          onPress={onImport}
          isDisabled={isLoading}
        />
      </div>

      {/* Card preview grid - truncated to 2 rows */}
      <div className="import-preview-grid">
        {displayCards.map((card, index) => (
          <div key={`${card.scryfallUuid}-${index}`} className="import-preview-item">
            <div className="import-thumbnail-wrapper">
              <img
                src={getCardImageUrl(card)}
                alt={card.cardName}
                className="import-thumbnail"
                loading="lazy"
                onError={() => handleImageError(card.scryfallUuid)}
              />
              {card.foilQuantity > 0 && <div className="import-foil-badge">Foil</div>}
            </div>
            <div className="import-preview-details">
              <span className="import-card-name" title={card.cardName}>
                {card.cardName}
              </span>
              <span className="import-card-qty">
                {card.quantity > 0 && card.foilQuantity > 0
                  ? `${card.quantity} + ${card.foilQuantity}F`
                  : card.foilQuantity > 0
                    ? `${card.foilQuantity}F`
                    : `x${card.quantity}`}
              </span>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <button className="import-show-more-btn" onClick={handleLoadMore} type="button">
          Show more ({(cards.length - displayCount).toLocaleString()} remaining)
        </button>
      )}

      {isPreviewOnly && (
        <p className="import-preview-note">
          * Showing preview of first {cards.length} cards
        </p>
      )}
    </div>
  );
};

export default CardPreview;
